import NumberInput from '../components/NumberInput.jsx'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronUp, Plug, RotateCcw, X, Plus, Zap, Layers, ArrowLeft } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { getCachedCandleMeta, setCachedCandles, isCacheFresh } from '../lib/candleCache'
import { useAuth } from '../hooks/useAuth'
import { useSettings } from '../hooks/useSettings'
import AccountName from '../components/AccountName'
import { usePrivacy } from '../hooks/usePrivacy'
import { buildPositionWorkspaceTarget } from '../lib/positionWorkspaceRoute'
import {
  fmt,
  calcMaxLoss,
  calcTrancheSizing,
  calcShares,
  calcUnifiedStop,
  checkStopBreach,
} from '../lib/calculations'

/* ── Constants ─────────────────────────────────────────────────────────────── */
const ALLOC_PRESETS = [
  { value: 3,   label: '3%' },
  { value: 4.5, label: '4.5% System' },
  { value: 9,   label: '9% System' },
  { value: 10,  label: '10%' },
  { value: 15,  label: '15%' },
  { value: 25,  label: '25%' },
  { value: 33,  label: '33%' },
]

const RISK_PRESETS = [
  { value: 5,    label: '5%' },
  { value: 7.5,  label: '7.5% System' },
  { value: 10,   label: '10%' },
  { value: 12.5, label: '12.5%' },
]

const PURCHASE_OPTIONS = [1, 3, 4, 5]

const DEFAULT_ENV_PRESETS = [
  { id: 'bull',    name: 'Bull Market',  alloc: 9,   risk: 7.5, tranches: 3 },
  { id: 'neutral', name: 'Neutral',      alloc: 4.5, risk: 7.5, tranches: 3 },
  { id: 'bear',    name: 'Bear Market',  alloc: 4.5, risk: 5,   tranches: 1 },
]

const TAG_LABELS = ['Initial Buy', 'Add-On 2', 'Add-On 3', 'Add-On 4', 'Add-On 5', 'Add-On 6']
const TAG_COLORS = [
  'bg-ink/10 text-ink border border-ink/20',
  'bg-forest/10 text-forest border border-forest/20',
  'bg-forest/10 text-forest border border-forest/20',
  'bg-forest/10 text-forest border border-forest/20',
  'bg-forest/10 text-forest border border-forest/20',
  'bg-forest/10 text-forest border border-forest/20',
]

const LS_KEY = 'bwc_env_presets'

/* ── Helpers ───────────────────────────────────────────────────────────────── */
function fmtEquity(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${n.toFixed(2)}`
}

function getAccountEquity(acct) {
  const eq = acct.equity || {}
  return eq.liquidationValue ?? eq.totalAccountValue ?? 0
}

function loadEnvPresets() {
  try {
    const stored = localStorage.getItem(LS_KEY)
    return stored ? JSON.parse(stored) : DEFAULT_ENV_PRESETS
  } catch { return DEFAULT_ENV_PRESETS }
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

function saveEnvPresetsToLS(presets) {
  localStorage.setItem(LS_KEY, JSON.stringify(presets))
}

function ResultBox({ label, value, accent, sub }) {
  return (
    <div className="bg-canvas2/50 border border-border rounded-lg p-4">
      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">{label}</div>
      <div className={`font-mono text-lg font-semibold ${accent || 'text-ink'}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-inkMuted mt-0.5">{sub}</div>}
    </div>
  )
}

/* ── Main Component ────────────────────────────────────────────────────────── */
export default function AccountsSizing() {
  const { user } = useAuth()
  const { settings, loading: settingsLoading, updateSettings } = useSettings()
  const { privacyMode } = usePrivacy()
  const mask = (val) => privacyMode ? '•••••' : val
  const navigate = useNavigate()

  // Accounts data
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [connected, setConnected] = useState(null)

  // Settings panel
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [envPresets, setEnvPresets] = useState(DEFAULT_ENV_PRESETS)
  const [editingPresetId, setEditingPresetId] = useState(null)
  const [editingName, setEditingName] = useState('')

  // Mode selector: null = landing, 'quick' = Quick Size, 'full' = Full Build
  const [mode, setMode] = useState(null)

  // Quick Size inputs
  const [qsSymbol, setQsSymbol] = useState('')
  const [qsEntry, setQsEntry] = useState('')
  const [qsStop, setQsStop] = useState('')

  // Pre-fetch candle data on symbol entry (1s debounce)
  const prefetchTimerRef = useRef(null)
  useEffect(() => {
    if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current)
    const sym = qsSymbol.trim().toUpperCase()
    if (!sym || sym.length < 1) return
    prefetchTimerRef.current = setTimeout(async () => {
      const barSizes = [
        { barSize: '1 day', duration: '1 Y' },
        { barSize: '30 mins', duration: '1 Y' },
      ]
      for (const { barSize, duration } of barSizes) {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.access_token) continue
          const existing = await getCachedCandleMeta(sym, barSize)
          if (existing && isCacheFresh(existing.fetchedAt, barSize, existing.latestBarTime)) continue
          const params = new URLSearchParams({ symbol: sym, barSize, duration, whatToShow: 'TRADES', useRTH: '1' })
          const res = await fetch(`/api/ibkr/candles?${params}`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          })
          if (!res.ok) continue
          const data = await res.json()
          if (data.bars?.length) await setCachedCandles(sym, barSize, data.bars)
        } catch { /* silent */ }
      }
    }, 1000)
    return () => { if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current) }
  }, [qsSymbol])

  // Position Builder
  const [entries, setEntries] = useState([])
  const [stopTypes, setStopTypes] = useState([])    // 'dollar' | 'pattern' per tranche
  const [patternStops, setPatternStops] = useState([]) // pattern stop price per tranche
  const [costBasis, setCostBasis] = useState('fifo')

  // Create Position Project
  const [saving, setSaving] = useState(false)

  // Landing screen: drafts & recent projects
  const [drafts, setDrafts] = useState([])
  const [recentProjects, setRecentProjects] = useState([])

  // Load env presets from localStorage
  useEffect(() => { setEnvPresets(loadEnvPresets()) }, [])

  // Load drafts & recent projects for landing screen
  useEffect(() => {
    if (!user) return
    const loadLandingData = async () => {
      const [{ data: draftData }, { data: recentData }] = await Promise.all([
        supabase.from('position_projects').select('*').eq('user_id', user.id).eq('is_draft', true).order('created_at', { ascending: false }).limit(10),
        supabase.from('position_projects').select('*').eq('user_id', user.id).eq('is_draft', false).order('created_at', { ascending: false }).limit(5),
      ])
      setDrafts(draftData || [])
      setRecentProjects(recentData || [])
    }
    loadLandingData()
  }, [user, mode])

  // Init cost basis from settings
  useEffect(() => {
    if (settings.cost_basis_method) setCostBasis(settings.cost_basis_method)
  }, [settings.cost_basis_method])

  /* ── Load Accounts ──────────────────────────────────────────────────────── */
  const loadAccounts = useCallback(async () => {
    if (!user) return
    if (accounts.length === 0) setLoading(true)
    const [{ data: tokenRow }, { data: accts }] = await Promise.all([
      supabase.from('broker_tokens').select('broker').eq('user_id', user.id).eq('broker', 'schwab').maybeSingle(),
      supabase.from('schwab_accounts').select('account_number, nick_name, account_type, equity').eq('user_id', user.id).order('account_number'),
    ])
    setConnected(!!tokenRow)
    setAccounts(accts || [])
    setLoading(false)
  }, [user])

  useEffect(() => { loadAccounts() }, [loadAccounts])

  // Auto-resize entries/stopTypes/patternStops when tranches change
  useEffect(() => {
    const count = settings.default_tranches || 3
    setEntries(prev => {
      const next = Array(count).fill(null)
      prev.forEach((v, i) => { if (i < count) next[i] = v })
      return next
    })
    setStopTypes(prev => {
      const next = Array(count).fill('dollar')
      prev.forEach((v, i) => { if (i < count) next[i] = v })
      return next
    })
    setPatternStops(prev => {
      const next = Array(count).fill(null)
      prev.forEach((v, i) => { if (i < count) next[i] = v })
      return next
    })
  }, [settings.default_tranches])

  /* ── Derived: Settings ──────────────────────────────────────────────────── */
  const allocPct = settings.default_alloc_pct
  const riskPct = settings.default_risk_pct
  const numTranches = settings.default_tranches || 3
  const equityRiskPct = (allocPct * riskPct) / 100

  const activeAllocPreset = ALLOC_PRESETS.find(p => p.value === allocPct)
  const activeRiskPreset = RISK_PRESETS.find(p => p.value === riskPct)
  const activeEnvPreset = envPresets.find(p =>
    p.alloc === allocPct && p.risk === riskPct && p.tranches === numTranches
  )

  /* ── Derived: Dashboard ─────────────────────────────────────────────────── */
  const totalEquity = accounts.reduce((sum, a) => sum + getAccountEquity(a), 0)
  const totalMaxLoss = accounts.reduce((sum, a) => sum + calcMaxLoss(getAccountEquity(a), allocPct, riskPct), 0)
  const totalPerTranche = accounts.reduce((sum, a) => sum + calcTrancheSizing(getAccountEquity(a), allocPct), 0)

  /* ── Derived: Position Builder ──────────────────────────────────────────── */
  const perTranche = totalEquity * (allocPct / 100)

  const filledTranches = useMemo(() => {
    return entries
      .map((price, i) => price > 0 ? { idx: i, price, shares: calcShares(perTranche, price) } : null)
      .filter(Boolean)
  }, [entries, perTranche])

  // Row-level cumulative dollar stops (for display in dollar-mode rows)
  const rowStops = useMemo(() => {
    const stops = {}
    for (let i = 0; i < numTranches; i++) {
      const cumTranches = entries
        .slice(0, i + 1)
        .map((price, j) => price > 0 ? { idx: j, price, shares: calcShares(perTranche, price) } : null)
        .filter(Boolean)
      if (cumTranches.length === i + 1) {
        const calc = calcUnifiedStop(cumTranches, riskPct)
        if (calc) stops[i] = calc.stopPrice
      }
    }
    return stops
  }, [entries, numTranches, perTranche, riskPct])

  // Dollar stop calc (used for avg cost, total shares, dollar stop price)
  const stopCalc = useMemo(() => calcUnifiedStop(filledTranches, riskPct), [filledTranches, riskPct])

  // Effective stop per filled tranche (pattern or dollar)
  const effectiveStops = useMemo(() => {
    if (!stopCalc) return {}
    const result = {}
    filledTranches.forEach(t => {
      const isPattern = stopTypes[t.idx] === 'pattern' && patternStops[t.idx] > 0
      result[t.idx] = isPattern ? patternStops[t.idx] : stopCalc.stopPrice
    })
    return result
  }, [stopCalc, filledTranches, stopTypes, patternStops])

  // Weighted effective stop price across entire position
  const effectiveStopPrice = useMemo(() => {
    if (filledTranches.length === 0 || !stopCalc) return null
    const totalShares = filledTranches.reduce((s, t) => s + t.shares, 0)
    if (totalShares === 0) return null
    const weighted = filledTranches.reduce((s, t) => {
      const stop = effectiveStops[t.idx] ?? stopCalc.stopPrice
      return s + stop * t.shares
    }, 0)
    return weighted / totalShares
  }, [filledTranches, effectiveStops, stopCalc])

  // Net P&L at effective stops
  const netAtStop = useMemo(() => {
    if (!stopCalc || filledTranches.length === 0) return 0
    return filledTranches.reduce((sum, t) => {
      const stop = effectiveStops[t.idx] ?? stopCalc.stopPrice
      return sum + (stop - t.price) * t.shares
    }, 0)
  }, [stopCalc, filledTranches, effectiveStops])

  const netAtStopPct = totalEquity > 0 ? (netAtStop / totalEquity) * 100 : 0

  // Stop distance and breach check using effective stop
  const effectiveStopDistPct = (stopCalc && effectiveStopPrice != null)
    ? ((stopCalc.avgCost - effectiveStopPrice) / stopCalc.avgCost) * 100
    : 0
  const isBreach = effectiveStopDistPct > riskPct + 0.05

  // Position status for unified stop banner
  const positionStatus = useMemo(() => {
    if (!stopCalc) return null
    if (netAtStop > 0) return { icon: '\u{1F512}', label: 'POSITION LOCKED INTO GAIN', color: 'text-forest', bg: 'bg-forest/10 border-forest/30' }
    if (Math.abs(netAtStop) < stopCalc.totalCost * 0.001) return { icon: '\u26A1', label: 'BREAKEVEN PROTECTED', color: 'text-ink', bg: 'bg-canvas2 border-ink/20' }
    return { icon: '\u26A0', label: `RISK: ${fmt(Math.abs(netAtStop))} (${Math.abs(netAtStopPct).toFixed(2)}% of equity)`, color: 'text-ember', bg: 'bg-ember/10 border-ember/30' }
  }, [stopCalc, netAtStop, netAtStopPct])

  // Profit target — uses actual risk from effective stops
  const profitTarget = useMemo(() => {
    if (!stopCalc || filledTranches.length === 0) return null
    const { avgCost, totalShares } = stopCalc
    const actualRiskAmount = Math.abs(netAtStop)
    const riskForTarget = actualRiskAmount > 0 ? actualRiskAmount : stopCalc.actualMaxLoss
    const gainPerShareNeeded = (2 * riskForTarget) / totalShares
    const targetPrice = avgCost + gainPerShareNeeded
    const sharesToSell = Math.round(totalShares / 3)
    const pctGainFromAvg = ((targetPrice - avgCost) / avgCost) * 100
    return {
      targetPrice,
      sharesToSell,
      sellValue: sharesToSell * targetPrice,
      gainAtTarget: totalShares * (targetPrice - avgCost),
      pctGainFromAvg,
      riskAmount: riskForTarget,
    }
  }, [stopCalc, filledTranches, netAtStop])

  // Per-tranche P&L at each tranche's effective stop
  const tranchePnL = useMemo(() => {
    if (!stopCalc || filledTranches.length === 0) return null
    const ordered = costBasis === 'lifo' ? [...filledTranches].reverse() : filledTranches
    return ordered.map(t => {
      const stop = effectiveStops[t.idx] ?? stopCalc.stopPrice
      const pnl = (stop - t.price) * t.shares
      const pnlPct = ((stop - t.price) / t.price) * 100
      return { ...t, pnl, pnlPct, stop, role: pnl >= 0 ? 'absorbing' : 'exposing' }
    })
  }, [stopCalc, filledTranches, costBasis, effectiveStops])

  // Capital deployed
  const capitalDeployed = stopCalc ? stopCalc.totalCost : 0
  const capitalRemaining = perTranche * numTranches - capitalDeployed
  const stopCushion = (stopCalc && effectiveStopPrice != null)
    ? ((stopCalc.avgCost - effectiveStopPrice) / stopCalc.avgCost) * 100
    : 0

  // Breakeven analysis
  const breakevenAnalysis = useMemo(() => {
    if (!stopCalc || filledTranches.length === 0 || effectiveStopPrice == null) return null
    const lastEntry = Math.max(...filledTranches.map(t => t.price))
    const cushionFromLast = ((lastEntry - stopCalc.avgCost) / lastEntry) * 100
    const riseNeeded = ((stopCalc.avgCost - effectiveStopPrice) / effectiveStopPrice) * 100
    const alreadyThere = effectiveStopPrice >= stopCalc.avgCost
    return { breakevenPrice: stopCalc.avgCost, cushionFromLast, lastEntry, riseNeeded, alreadyThere }
  }, [stopCalc, filledTranches, effectiveStopPrice])

  /* ── Handlers ───────────────────────────────────────────────────────────── */
  const applyEnvPreset = (preset) => {
    updateSettings({ default_alloc_pct: preset.alloc, default_risk_pct: preset.risk, default_tranches: preset.tranches })
  }

  const saveCurrentAsPreset = () => {
    const newPreset = { id: `custom_${Date.now()}`, name: `Custom ${envPresets.length + 1}`, alloc: allocPct, risk: riskPct, tranches: numTranches }
    const updated = [...envPresets, newPreset]
    setEnvPresets(updated)
    saveEnvPresetsToLS(updated)
  }

  const deletePreset = (id) => {
    const updated = envPresets.filter(p => p.id !== id)
    setEnvPresets(updated)
    saveEnvPresetsToLS(updated)
  }

  const startRenamePreset = (preset) => {
    setEditingPresetId(preset.id)
    setEditingName(preset.name)
  }

  const finishRenamePreset = () => {
    if (editingPresetId && editingName.trim()) {
      const updated = envPresets.map(p => p.id === editingPresetId ? { ...p, name: editingName.trim() } : p)
      setEnvPresets(updated)
      saveEnvPresetsToLS(updated)
    }
    setEditingPresetId(null)
    setEditingName('')
  }

  const resetBuilder = () => {
    setEntries(Array(numTranches).fill(null))
    setStopTypes(Array(numTranches).fill('dollar'))
    setPatternStops(Array(numTranches).fill(null))
  }

  const setTrancheStopType = (i, type) => {
    setStopTypes(prev => { const next = [...prev]; next[i] = type; return next })
  }

  const setPatternStop = (i, val) => {
    setPatternStops(prev => { const next = [...prev]; next[i] = val; return next })
  }

  const getAccountName = (acct) => acct.nick_name || `···${acct.account_number.slice(-4)}`

  const createQuickSizeProject = async () => {
    if (!user || !qsReady || saving) return
    setSaving(true)
    const sizingData = qsAccountRows.map(row => ({
      name: getAccountName(row.acct),
      account_number: row.acct.account_number,
      shares: row.shares,
      max_loss: row.maxLoss,
      equity: row.equity,
      tranche_size: row.trancheSize,
    }))
    const { error } = await supabase.from('position_projects').insert({
      user_id: user.id,
      symbol: qsSymbol,
      entry_price: qsEntryNum,
      stop_price: qsStopNum,
      stop_type: 'dollar',
      allocation_pct: allocPct,
      risk_pct: riskPct,
      num_purchases: numTranches,
      market_environment: activeEnvPreset ? activeEnvPreset.name : null,
      account_sizing: sizingData,
      stage: 'planned',
      is_draft: false,
      draft_mode: 'quick',
    })
    setSaving(false)
    if (!error) navigate('/positions/board')
  }

  const createFullBuildProject = async () => {
    if (!user || !stopCalc || saving) return
    setSaving(true)
    const sizingData = accounts.map(acct => {
      const acctEquity = getAccountEquity(acct)
      const acctPer = acctEquity * (allocPct / 100)
      const trancheShares = filledTranches.map(t => ({
        tranche: t.idx,
        price: t.price,
        shares: calcShares(acctPer, t.price),
      }))
      const totalShares = trancheShares.reduce((s, t) => s + t.shares, 0)
      const maxLoss = totalShares * Math.abs(stopCalc.avgCost - (effectiveStopPrice ?? stopCalc.stopPrice))
      return {
        name: getAccountName(acct),
        account_number: acct.account_number,
        shares: totalShares,
        max_loss: maxLoss,
        equity: acctEquity,
        tranches: trancheShares,
      }
    })
    const firstEntry = filledTranches.length > 0 ? filledTranches[0].price : null
    const { error } = await supabase.from('position_projects').insert({
      user_id: user.id,
      symbol: qsSymbol || 'UNKNOWN',
      entry_price: firstEntry,
      stop_price: effectiveStopPrice ?? stopCalc?.stopPrice ?? null,
      stop_type: stopTypes.some(t => t === 'pattern') ? 'pattern' : 'dollar',
      allocation_pct: allocPct,
      risk_pct: riskPct,
      num_purchases: numTranches,
      market_environment: activeEnvPreset ? activeEnvPreset.name : null,
      account_sizing: sizingData,
      stage: 'planned',
      is_draft: false,
      draft_mode: 'full',
    })
    setSaving(false)
    if (!error) navigate('/positions/board')
  }

  /* ── Quick Size derived values ─────────────────────────────────────────── */
  const qsEntryNum = Number(qsEntry)
  const qsStopNum = Number(qsStop)
  const qsReady = qsEntryNum > 0 && qsStopNum > 0 && qsStopNum < qsEntryNum
  const qsRiskPerShare = qsReady ? qsEntryNum - qsStopNum : 0
  const qsStopDistPct = qsReady ? ((qsEntryNum - qsStopNum) / qsEntryNum * 100) : 0

  const qsAccountRows = useMemo(() => {
    if (!qsReady) return []
    return accounts.map(acct => {
      const equity = getAccountEquity(acct)
      const trancheSize = calcTrancheSizing(equity, allocPct)
      const shares = calcShares(trancheSize, qsEntryNum)
      const maxLoss = shares * qsRiskPerShare
      const riskPct = equity > 0 ? (maxLoss / equity) * 100 : 0
      return { acct, equity, trancheSize, shares, maxLoss, riskPct }
    })
  }, [qsReady, accounts, allocPct, qsEntryNum, qsRiskPerShare])

  const qsTotalShares = qsAccountRows.reduce((s, r) => s + r.shares, 0)
  const qsTotalMaxLoss = qsAccountRows.reduce((s, r) => s + r.maxLoss, 0)
  const qsTotalRiskPct = totalEquity > 0 ? (qsTotalMaxLoss / totalEquity) * 100 : 0

  /* ── Loading / Empty States ─────────────────────────────────────────────── */
  if (loading || settingsLoading) {
    return <div className="font-mono text-xs text-inkMuted animate-pulse">Loading…</div>
  }

  /* ── Mode Selector Landing ──────────────────────────────────────────────── */
  if (mode === null) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center min-h-[420px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-2xl">
            {/* Quick Size Card */}
            <button
              onClick={() => setMode('quick')}
              className="bg-canvas border border-border rounded-xl p-8 flex flex-col items-center text-center gap-4 hover:border-forest hover:bg-canvas2/30 transition-all cursor-pointer group min-h-[220px] justify-center"
            >
              <div className="w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center group-hover:bg-forest/20 transition-colors">
                <Zap size={32} className="text-forest" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-black text-lg uppercase tracking-wide text-ink mb-1.5">Quick Size</div>
                <div className="font-mono text-xs text-inkMuted leading-relaxed">
                  I need share counts in 30 seconds.<br />Symbol, entry, stop — go.
                </div>
              </div>
            </button>

            {/* Full Build Card */}
            <button
              onClick={() => setMode('full')}
              className="bg-canvas border border-border rounded-xl p-8 flex flex-col items-center text-center gap-4 hover:border-forest hover:bg-canvas2/30 transition-all cursor-pointer group min-h-[220px] justify-center"
            >
              <div className="w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center group-hover:bg-forest/20 transition-colors">
                <Layers size={32} className="text-forest" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-black text-lg uppercase tracking-wide text-ink mb-1.5">Full Build</div>
                <div className="font-mono text-xs text-inkMuted leading-relaxed">
                  Plan a full position with tranches,<br />targets, and project creation.
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Drafts */}
        {drafts.length > 0 && (
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Drafts</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {drafts.map(d => (
                <button
                  key={d.id}
                  onClick={() => setMode(d.draft_mode === 'full' ? 'full' : 'quick')}
                  className="bg-canvas border border-border rounded-xl px-4 py-3 text-left hover:border-ink/40 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-black text-sm uppercase tracking-wide text-ink">{d.symbol}</span>
                    <span className="font-mono text-[9px] px-2 py-0.5 rounded-full border bg-ember/10 text-ember border-ember/20 uppercase tracking-widest">Draft</span>
                    <span className="font-mono text-[9px] px-2 py-0.5 rounded-full border bg-canvas2 text-inkMuted border-border">
                      {d.draft_mode === 'full' ? 'Full Build' : 'Quick Size'}
                    </span>
                  </div>
                  <div className="flex gap-3 text-[10px] font-mono text-inkMuted">
                    {d.entry_price != null && <span>Entry {fmt(d.entry_price)}</span>}
                    {d.stop_price != null && <span>Stop {fmt(d.stop_price)}</span>}
                    <span className="ml-auto text-inkDim">{timeAgo(d.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Recent Projects */}
        {recentProjects.length > 0 && (
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Recent Projects</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentProjects.map(p => {
                const stageBadge = {
                  idea: 'bg-canvas2 text-inkMuted border-border',
                  planned: 'bg-ink/10 text-ink border-ink/20',
                  active: 'bg-forest/10 text-forest border-forest/20',
                  closed: 'bg-canvas2 text-inkMuted border-border',
                  reviewed: 'bg-canvas2 text-inkDim border-border',
                }[p.stage] || 'bg-canvas2 text-inkMuted border-border'
                return (
                  <Link
                    key={p.id}
                    to={buildPositionWorkspaceTarget({ symbol: p.symbol, projectId: p.id })}
                    className="bg-canvas border border-border rounded-xl px-4 py-3 text-left hover:border-ink/40 transition-all"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-black text-sm uppercase tracking-wide text-ink">{p.symbol}</span>
                      <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${stageBadge}`}>
                        {p.stage}
                      </span>
                    </div>
                    <div className="flex gap-3 text-[10px] font-mono text-inkMuted">
                      {p.entry_price != null && <span>Entry {fmt(p.entry_price)}</span>}
                      {p.stop_price != null && <span>Stop {fmt(p.stop_price)}</span>}
                      <span className="ml-auto text-inkDim">{timeAgo(p.created_at)}</span>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Empty state when no drafts and no recent projects */}
        {drafts.length === 0 && recentProjects.length === 0 && (
          <div className="bg-canvas border border-border rounded-xl px-6 py-8 text-center">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Recent Projects</div>
            <div className="font-mono text-xs text-inkMuted">
              No position projects yet. Create one from Quick Size or Full Build above.
            </div>
          </div>
        )}
      </div>
    )
  }

  /* ── Quick Size Mode ────────────────────────────────────────────────────── */
  if (mode === 'quick') {
    return (
      <div className="space-y-6">
        {/* Back button + mode label */}
        <button
          onClick={() => setMode(null)}
          className="flex items-center gap-2 font-mono text-xs text-inkMuted hover:text-ink transition-colors"
        >
          <ArrowLeft size={14} />
          <span>Back to Mode Select</span>
        </button>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="text-forest" strokeWidth={1.5} />
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Quick Size</div>
          </div>
          <h2 className="font-black text-xl uppercase tracking-wide text-ink">30-Second Sizing</h2>
        </div>

        {/* Inputs */}
        <div className="bg-canvas border border-border rounded-xl px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5 block">Symbol</label>
              <input
                type="text"
                value={qsSymbol}
                onChange={e => setQsSymbol(e.target.value.toUpperCase())}
                placeholder="AAPL"
                className="font-mono text-sm px-3 py-2 rounded-lg bg-canvas2 border border-border text-ink w-full uppercase tracking-wide"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5 block">Entry Price</label>
              <input
                type="number"
                step="0.01"
                value={qsEntry}
                onChange={e => setQsEntry(e.target.value)}
                placeholder="0.00"
                className="font-mono text-sm pl-3 pr-4 py-2 rounded-lg bg-canvas2 border border-border text-ink w-full text-right"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5 block">Stop Price</label>
              <input
                type="number"
                step="0.01"
                value={qsStop}
                onChange={e => setQsStop(e.target.value)}
                placeholder="0.00"
                className="font-mono text-sm pl-3 pr-4 py-2 rounded-lg bg-canvas2 border border-border text-ink w-full text-right"
              />
            </div>
          </div>

          {/* Inline settings toolbar */}
          <div className="mt-3 pt-3 border-t border-border space-y-2.5">

            {/* Market Environment */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mr-1">Env</span>
              {envPresets.map(p => (
                <button
                  key={p.id}
                  onClick={() => applyEnvPreset(p)}
                  className={`font-mono text-[10px] px-2 py-1 rounded-md border transition-all ${
                    activeEnvPreset?.id === p.id
                      ? 'bg-ink text-parchment border-ink'
                      : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>

            {/* Allocation / Risk / Purchases */}
            <div className="flex items-center gap-4 flex-wrap">

              {/* Allocation % */}
              <div className="flex items-center gap-1">
                <span className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mr-0.5">Alloc</span>
                {ALLOC_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => updateSettings({ default_alloc_pct: p.value })}
                    className={`font-mono text-[10px] px-1.5 py-1 rounded-md border transition-all ${
                      allocPct === p.value
                        ? 'bg-ink text-parchment border-ink'
                        : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Risk % */}
              <div className="flex items-center gap-1">
                <span className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mr-0.5">Risk</span>
                {RISK_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => updateSettings({ default_risk_pct: p.value })}
                    className={`font-mono text-[10px] px-1.5 py-1 rounded-md border transition-all ${
                      riskPct === p.value
                        ? 'bg-ink text-parchment border-ink'
                        : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Purchases */}
              <div className="flex items-center gap-1">
                <span className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mr-0.5">Buys</span>
                {PURCHASE_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => updateSettings({ default_tranches: n })}
                    className={`font-mono text-[10px] px-2 py-1 rounded-md border transition-all ${
                      numTranches === n
                        ? 'bg-ink text-parchment border-ink'
                        : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>

            </div>
          </div>
        </div>

        {/* Results */}
        {qsReady && (
          <>
            {/* Summary bar */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-canvas border border-border rounded-xl px-5 py-4">
                <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Stop Distance</div>
                <div className="font-mono text-lg font-semibold text-ember">
                  {mask(fmt(qsRiskPerShare))} <span className="text-xs text-inkMuted">({qsStopDistPct.toFixed(2)}%)</span>
                </div>
              </div>
              <div className="bg-canvas border border-border rounded-xl px-5 py-4">
                <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Risk Per Share</div>
                <div className="font-mono text-lg font-semibold text-ink">{mask(fmt(qsRiskPerShare))}</div>
              </div>
            </div>

            {/* Account sizing table */}
            <div className="bg-canvas border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="px-4 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted text-left">Account</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted text-right">Equity</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted text-right">Per Tranche</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted text-right">Shares</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted text-right">Max $ Loss</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted text-right">% at Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qsAccountRows.map((row, i) => (
                      <tr key={row.acct.account_number} className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-canvas2/20'}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-ink">
                          <AccountName accountNumber={row.acct.account_number} />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-right text-inkMuted">{mask(fmtEquity(row.equity))}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-right text-ink">{mask(fmt(row.trancheSize))}</td>
                        <td className="px-4 py-2.5 font-mono text-sm text-right font-semibold text-ink">{row.shares.toLocaleString()}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-right text-ember">{mask(fmt(row.maxLoss))}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-right text-inkMuted">{row.riskPct.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-canvas2">
                      <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-inkMuted font-semibold">Total</td>
                      <td className="px-4 py-3 font-mono text-xs text-right text-inkMuted">{mask(fmtEquity(totalEquity))}</td>
                      <td className="px-4 py-3 font-mono text-xs text-right text-ink font-semibold">{mask(fmt(totalPerTranche))}</td>
                      <td className="px-4 py-3 font-mono text-sm text-right font-bold text-ink">{qsTotalShares.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-xs text-right font-semibold text-ember">{mask(fmt(qsTotalMaxLoss))}</td>
                      <td className="px-4 py-3 font-mono text-xs text-right font-semibold text-inkMuted">{qsTotalRiskPct.toFixed(2)}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Create Position Project button */}
            <div className="flex justify-end">
              <button
                onClick={createQuickSizeProject}
                disabled={saving}
                className="btn-primary flex items-center gap-1.5"
              >
                {saving ? 'Creating...' : 'Create Position Project'}
              </button>
            </div>
          </>
        )}
      </div>
    )
  }

  /* ── Full Build Mode ────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">

      {/* Back button + mode label */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setMode(null)}
          className="flex items-center gap-2 font-mono text-xs text-inkMuted hover:text-ink transition-colors"
        >
          <ArrowLeft size={14} />
          <span>Back to Mode Select</span>
        </button>
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-forest" strokeWidth={1.5} />
          <span className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Full Build</span>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 1 — Position Settings (collapsible)
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-canvas2/30 transition-colors"
        >
          <div className="text-left">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Market Environment</div>
            <h2 className="font-black text-base uppercase tracking-wide text-ink">Position Settings</h2>
          </div>
          <div className="flex items-center gap-2">
            {!settingsOpen && (
              <div className="flex gap-1.5">
                {[`Alloc: ${allocPct}%`, `Risk: ${riskPct}%`, `Tranches: ${numTranches}`].map(chip => (
                  <span key={chip} className="font-mono text-[10px] px-2.5 py-1 rounded-full bg-canvas2 text-inkMuted border border-border">
                    {chip}
                  </span>
                ))}
              </div>
            )}
            {settingsOpen
              ? <ChevronUp size={16} className="text-inkMuted" />
              : <ChevronDown size={16} className="text-inkMuted" />}
          </div>
        </button>

        {settingsOpen && (
          <div className="px-6 pb-6 border-t border-border pt-5 space-y-6">

            {/* Market Environment Presets */}
            <div>
              <div className="label mb-2">Market Environment</div>
              <div className="flex flex-wrap gap-2 items-center">
                {envPresets.map(p => (
                  <div key={p.id} className="flex items-center">
                    {editingPresetId === p.id ? (
                      <input
                        autoFocus
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={finishRenamePreset}
                        onKeyDown={e => { if (e.key === 'Enter') finishRenamePreset(); if (e.key === 'Escape') { setEditingPresetId(null); setEditingName('') } }}
                        className="font-mono text-xs px-3 py-2 rounded-l-lg border border-ink bg-canvas2 text-ink outline-none w-28"
                      />
                    ) : (
                      <button
                        onClick={() => applyEnvPreset(p)}
                        onDoubleClick={() => startRenamePreset(p)}
                        className={`font-mono text-xs px-3 py-2 rounded-l-lg border transition-all ${
                          activeEnvPreset?.id === p.id
                            ? 'bg-ink text-parchment border-ink'
                            : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                        }`}
                        title="Double-click to rename"
                      >
                        {p.name}
                      </button>
                    )}
                    <button
                      onClick={() => deletePreset(p.id)}
                      className="font-mono text-[10px] px-1.5 py-2 rounded-r-lg border border-l-0 border-border text-inkMuted hover:text-ember hover:border-ember transition-colors"
                      title="Delete preset"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={saveCurrentAsPreset}
                  className="font-mono text-xs px-3 py-2 rounded-lg border border-dashed border-border text-inkMuted hover:border-ink hover:text-ink transition-all flex items-center gap-1"
                >
                  <Plus size={10} />
                  Save Current
                </button>
              </div>
            </div>

            {/* Allocation % */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label">Allocation %</label>
                <span className="font-mono text-2xl font-semibold text-ink">{allocPct}%</span>
              </div>
              <input
                type="range" min={4.5} max={50} step={0.5}
                value={allocPct}
                onChange={e => updateSettings({ default_alloc_pct: parseFloat(e.target.value) })}
                className="w-full accent-ink h-1.5 mb-2"
              />
              <div className="flex flex-wrap gap-1.5">
                {ALLOC_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => updateSettings({ default_alloc_pct: p.value })}
                    className={`font-mono text-[10px] px-2.5 py-1.5 rounded-lg border transition-all ${
                      allocPct === p.value
                        ? 'bg-ink text-parchment border-ink'
                        : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  className={`font-mono text-[10px] px-2.5 py-1.5 rounded-lg border transition-all ${
                    !activeAllocPreset
                      ? 'bg-ink text-parchment border-ink'
                      : 'bg-canvas2 text-inkMuted border-border'
                  }`}
                >
                  Custom
                </button>
              </div>
            </div>

            {/* Risk Per Trade % */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label">Risk Per Trade %</label>
                <span className="font-mono text-2xl font-semibold text-ink">{riskPct}%</span>
              </div>
              <input
                type="range" min={1} max={15} step={0.25}
                value={riskPct}
                onChange={e => updateSettings({ default_risk_pct: parseFloat(e.target.value) })}
                className="w-full accent-ink h-1.5 mb-2"
              />
              <div className="flex flex-wrap gap-1.5 mb-2">
                {RISK_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => updateSettings({ default_risk_pct: p.value })}
                    className={`font-mono text-[10px] px-2.5 py-1.5 rounded-lg border transition-all ${
                      riskPct === p.value
                        ? 'bg-ink text-parchment border-ink'
                        : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="font-mono text-xs text-inkMuted">
                = <strong className="text-ink">{equityRiskPct.toFixed(3)}%</strong> of equity at risk per position
              </div>
            </div>

            {/* Number of Purchases */}
            <div>
              <label className="label block mb-2">Number of Purchases</label>
              <div className="flex flex-wrap gap-1.5 items-center">
                {PURCHASE_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => updateSettings({ default_tranches: n })}
                    className={`font-mono text-xs px-4 py-2 rounded-lg border transition-all ${
                      numTranches === n
                        ? 'bg-ink text-parchment border-ink'
                        : 'bg-canvas2 text-inkMuted border-border hover:border-ink hover:text-ink'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <span className="font-mono text-[10px] text-inkMuted ml-2 bg-canvas2 border border-border rounded-full px-3 py-1">
                  Splitting into {numTranches} purchase{numTranches !== 1 ? 's' : ''} expands effective stop room
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Not Connected / No Data ────────────────────────────────────────── */}
      {!connected && (
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <Plug size={24} className="text-border mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-mono text-xs text-inkMuted mb-1">No broker connected</div>
          <Link to="/integrations" className="font-mono text-xs text-ink underline">
            Connect Schwab to sync accounts →
          </Link>
        </div>
      )}

      {connected && accounts.length === 0 && (
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <div className="font-mono text-xs text-inkMuted mb-1">No account data synced yet</div>
          <Link to="/integrations" className="font-mono text-xs text-ink underline">
            Sync from Integrations →
          </Link>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 2 — Sizing Dashboard
          ═══════════════════════════════════════════════════════════════════════ */}
      {accounts.length > 0 && (
        <div className="bg-canvas border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">
              {activeEnvPreset ? activeEnvPreset.name : 'Custom'} · {allocPct}% Alloc · {riskPct}% Risk · {numTranches}T
            </div>
            <h2 className="font-black text-base uppercase tracking-wide text-ink">
              {accounts.length} Account{accounts.length !== 1 ? 's' : ''} · Position Sizes
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['Account', 'Equity', 'Max $ Loss', 'Per Tranche', 'Full Pos 10%', 'Full Pos 15%', 'Full Pos 25%'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map((acct, i) => {
                  const equity = getAccountEquity(acct)
                  const maxLoss = calcMaxLoss(equity, allocPct, riskPct)
                  const tranche = calcTrancheSizing(equity, allocPct)
                  return (
                    <tr key={acct.account_number} className={`border-b border-border last:border-0 hover:bg-canvas2 transition-colors ${i % 2 === 0 ? '' : 'bg-canvas2/20'}`}>
                      <td className="px-4 py-4">
                        <AccountName accountNumber={acct.account_number} className="font-mono text-sm font-semibold text-ink" />
                      </td>
                      <td className="px-4 py-4 font-mono text-sm font-semibold text-ink">{mask(fmtEquity(equity))}</td>
                      <td className="px-4 py-4 font-mono text-sm text-ember font-semibold">{mask(equity > 0 ? fmt(maxLoss) : '—')}</td>
                      <td className="px-4 py-4 font-mono text-sm font-semibold">{mask(equity > 0 ? fmt(tranche) : '—')}</td>
                      <td className="px-4 py-4 font-mono text-sm font-semibold text-forest">{mask(equity > 0 ? fmtEquity(equity * 0.10) : '—')}</td>
                      <td className="px-4 py-4 font-mono text-sm font-semibold text-forest">{mask(equity > 0 ? fmtEquity(equity * 0.15) : '—')}</td>
                      <td className="px-4 py-4 font-mono text-sm font-semibold text-forest">{mask(equity > 0 ? fmtEquity(equity * 0.25) : '—')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-border bg-canvas2 flex flex-wrap gap-8">
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Combined Equity</div>
              <div className="font-mono text-xl font-semibold text-ink">{mask(fmtEquity(totalEquity))}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Total Max Loss</div>
              <div className="font-mono text-xl font-semibold text-ember">{mask(fmt(totalMaxLoss))}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Total Per Tranche</div>
              <div className="font-mono text-xl font-semibold text-ink">{mask(fmt(totalPerTranche))}</div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 3 — Position Builder
          ═══════════════════════════════════════════════════════════════════════ */}
      {accounts.length > 0 && (
        <div className="bg-canvas border border-border rounded-xl overflow-hidden">

          {/* Header with FIFO/LIFO + Reset */}
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Position Builder</div>
              <h2 className="font-black text-base uppercase tracking-wide text-ink">Build Your Position</h2>
              <p className="font-mono text-xs text-inkMuted mt-1">
                Enter entry price for each tranche. Dollar stop recalculates across the <em>entire</em> position. Pattern stop uses your defined failure level.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1 bg-canvas2 border border-border rounded-lg p-1">
                <span className="font-mono text-[10px] text-inkMuted px-2">Cost Basis</span>
                {['fifo', 'lifo'].map(m => (
                  <button key={m} onClick={() => setCostBasis(m)}
                    className={`font-mono text-xs px-3 py-1.5 rounded-md transition-all ${
                      costBasis === m ? 'bg-ink text-parchment' : 'text-inkMuted hover:text-ink'
                    }`}>
                    {m.toUpperCase()}
                  </button>
                ))}
              </div>
              <button onClick={resetBuilder} className="btn-secondary text-xs py-2 flex items-center gap-1.5">
                <RotateCcw size={12} />
                Reset
              </button>
            </div>
          </div>

          {/* Tranche Rows */}
          <div>
            {Array.from({ length: numTranches }).map((_, i) => {
              const price = entries[i]
              const dollarStop = rowStops[i]
              const stopType = stopTypes[i] || 'dollar'
              const patternStop = patternStops[i]
              return (
                <div key={i} className={`px-6 py-4 border-b border-border last:border-0 grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_1fr] gap-4 items-start ${i > 0 && !entries[i - 1] ? 'opacity-50' : ''}`}>

                  {/* Tag */}
                  <div className="flex items-center pt-6">
                    <span className={`font-mono text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider whitespace-nowrap ${TAG_COLORS[i] || TAG_COLORS[1]}`}>
                      {TAG_LABELS[i] || `Add-On ${i + 1}`}
                    </span>
                  </div>

                  {/* Entry Price */}
                  <div>
                    <div className="font-mono text-[10px] text-inkMuted uppercase tracking-wider mb-1.5">Entry Price</div>
                    <div className="flex items-center gap-1.5 bg-canvas2 border border-border rounded-lg px-3 py-2 focus-within:border-ink transition-colors">
                      <span className="font-mono text-xs text-inkMuted">$</span>
                      <NumberInput
                        allowEmpty step="0.01" min="0.01" placeholder="0.00"
                        value={price ?? null}
                        onValueChange={val => {
                          setEntries(prev => { const next = [...prev]; next[i] = val; return next })
                        }}
                        className="bg-transparent font-mono text-sm text-ink outline-none w-full"
                      />
                    </div>
                  </div>

                  {/* Stop — Dollar or Pattern */}
                  <div>
                    {/* Stop type toggle */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="flex items-center bg-canvas2 border border-border rounded-md p-0.5">
                        {['dollar', 'pattern'].map(type => (
                          <button
                            key={type}
                            onClick={() => setTrancheStopType(i, type)}
                            className={`font-mono text-[10px] px-2.5 py-0.5 rounded transition-all ${
                              stopType === type
                                ? 'bg-ink text-parchment'
                                : 'text-inkMuted hover:text-ink'
                            }`}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                      <span className="font-mono text-[10px] text-inkMuted uppercase tracking-wider">Stop</span>
                    </div>

                    {stopType === 'dollar' ? (
                      /* Dollar stop display */
                      <>
                        <div className={`font-mono text-xl font-semibold ${dollarStop ? 'text-ember' : 'text-inkMuted'}`}>
                          {mask(dollarStop ? fmt(dollarStop) : '——')}
                        </div>
                        <div className="font-mono text-[10px] text-inkMuted mt-0.5">
                          {dollarStop && price ? `${((price - dollarStop) / price * 100).toFixed(1)}% below this entry` : '—'}
                        </div>
                      </>
                    ) : (
                      /* Pattern stop input */
                      <>
                        <div className="flex items-center gap-1.5 bg-canvas2 border border-border rounded-lg px-3 py-2 focus-within:border-ink transition-colors">
                          <span className="font-mono text-xs text-inkMuted">$</span>
                          <NumberInput
                            allowEmpty step="0.01" min="0.01" placeholder="0.00"
                            value={patternStop ?? null}
                            onValueChange={val => {
                              setPatternStop(i, val)
                            }}
                            className="bg-transparent font-mono text-sm text-ink outline-none w-full"
                          />
                        </div>
                        <div className="font-mono text-[10px] text-inkMuted mt-0.5">
                          {patternStop && price ? `${((price - patternStop) / price * 100).toFixed(1)}% below entry` : '—'}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Shares Per Account */}
                  <div>
                    <div className="font-mono text-[10px] text-inkMuted uppercase tracking-wider mb-1.5">Shares Per Account</div>
                    <div className="flex gap-3 flex-wrap">
                      {accounts.map(acct => {
                        const acctEquity = getAccountEquity(acct)
                        const acctPer = acctEquity * (allocPct / 100)
                        const shares = price > 0 && acctEquity > 0 ? calcShares(acctPer, price) : null
                        return (
                          <div key={acct.account_number} className="text-center">
                            <AccountName accountNumber={acct.account_number} className="font-mono text-[9px] text-inkMuted uppercase tracking-wider mb-0.5" />
                            <div className={`font-mono text-sm font-bold ${shares ? 'text-ink' : 'text-inkMuted'}`}>
                              {mask(shares ?? '——')}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* ── Result Sections (below tranche rows) ──────────────────────── */}
          <div className="px-6 py-5 space-y-5">

            {/* 1. STOP DISTANCE WARNING */}
            {isBreach && stopCalc && (
              <div className="bg-ember/5 border border-ember/30 rounded-xl px-5 py-4 font-mono text-xs">
                <div className="font-bold text-ember tracking-widest uppercase mb-1">
                  ⚠ Stop Distance Breach — Max {riskPct}%
                </div>
                <div className="text-inkMuted">
                  Effective stop ({mask(effectiveStopPrice != null ? `$${effectiveStopPrice.toFixed(2)}` : '—')}) is <strong className="text-ember">{effectiveStopDistPct.toFixed(1)}%</strong> below avg cost of {mask(fmt(stopCalc.avgCost))}.
                  Consider reducing position size or tightening your entry.
                </div>
              </div>
            )}

            {/* 2. PROFIT TARGET */}
            {profitTarget && stopCalc && (
              <div className="bg-forest/5 border border-forest/20 rounded-xl px-5 py-4 font-mono">
                <div className="text-[10px] tracking-widest uppercase text-forest mb-3">
                  2× Trade Risk Profit Target — Sell ⅓ of Position
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Target Price</div>
                    <div className="text-xl font-semibold text-forest">{mask(fmt(profitTarget.targetPrice))}</div>
                    <div className="text-[10px] text-inkMuted">+{profitTarget.pctGainFromAvg.toFixed(1)}% from avg cost</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Shares to Sell</div>
                    <div className="text-xl font-semibold text-ink">{mask(profitTarget.sharesToSell.toLocaleString())}</div>
                    <div className="text-[10px] text-inkMuted">⅓ of {mask(stopCalc.totalShares)} total · {mask(fmt(profitTarget.sellValue))}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Position Gain at Target</div>
                    <div className="text-xl font-semibold text-forest">+{mask(fmt(profitTarget.gainAtTarget))}</div>
                    <div className="text-[10px] text-inkMuted">= 2× your {mask(fmt(profitTarget.riskAmount))} risk</div>
                  </div>
                </div>
                {/* Shares to Sell Per Account */}
                <div className="border-t border-border/50 pt-3">
                  <div className="text-[9px] tracking-widest uppercase text-inkMuted mb-2">Shares to Sell Per Account</div>
                  <div className="flex gap-5 flex-wrap">
                    {accounts.map(acct => {
                      const acctEquity = getAccountEquity(acct)
                      const acctPer = acctEquity * (allocPct / 100)
                      const acctTotal = filledTranches.reduce((sum, t) => sum + calcShares(acctPer, t.price), 0)
                      const acctSell = acctEquity > 0 ? Math.round(acctTotal / 3) : null
                      return (
                        <div key={acct.account_number} className="text-center">
                          <AccountName accountNumber={acct.account_number} className="text-[9px] text-inkMuted uppercase tracking-wider mb-0.5" />
                          <div className={`text-sm font-bold ${acctSell ? 'text-ink' : 'text-inkMuted'}`}>
                            {mask(acctSell ?? '——')}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* 3. UNIFIED STOP BANNER */}
            {stopCalc && positionStatus && effectiveStopPrice != null && (
              <div className={`border rounded-xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${positionStatus.bg}`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{positionStatus.icon}</span>
                  <span className={`font-mono text-xs font-bold tracking-widest uppercase ${positionStatus.color}`}>
                    {positionStatus.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-5">
                  {[
                    { label: 'Effective Stop', value: mask(`$${effectiveStopPrice.toFixed(2)}`) },
                    { label: 'Avg Cost', value: mask(`$${stopCalc.avgCost.toFixed(2)}`) },
                    { label: 'Net at Stop', value: mask((netAtStop >= 0 ? '+' : '') + fmt(netAtStop)) },
                    { label: 'Total Shares', value: mask(stopCalc.totalShares.toLocaleString()) },
                  ].map(item => (
                    <div key={item.label} className="text-right">
                      <div className="font-mono text-[9px] text-inkMuted uppercase tracking-wider">{item.label}</div>
                      <div className="font-mono text-base font-bold text-ink">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 4. PER-TRANCHE P&L TABLE */}
            {tranchePnL && tranchePnL.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">
                  Per-Tranche P&L at Stop
                </div>
                <div className="bg-canvas2/50 border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        {['Tranche', 'Entry', 'Shares', 'Stop', 'P&L $', 'P&L %', 'Role'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tranchePnL.map((r, displayIdx) => {
                        const pnlColor = r.pnl >= 0 ? 'text-forest' : 'text-ember'
                        const isFirst = displayIdx === 0
                        const isLast = displayIdx === tranchePnL.length - 1
                        const isPattern = stopTypes[r.idx] === 'pattern'
                        return (
                          <tr key={r.idx} className="border-b border-border last:border-0">
                            <td className="px-3 py-2">
                              <span className={`font-mono text-[10px] px-2 py-1 rounded-full uppercase tracking-wider ${TAG_COLORS[r.idx] || TAG_COLORS[1]}`}>
                                {TAG_LABELS[r.idx] || `Add-On ${r.idx + 1}`}
                              </span>
                              {costBasis === 'lifo' && isFirst && (
                                <span className="ml-1.5 font-mono text-[9px] px-1.5 py-0.5 rounded bg-ink/10 text-ink uppercase">Sold 1st</span>
                              )}
                              {costBasis === 'lifo' && isLast && tranchePnL.length > 1 && (
                                <span className="ml-1.5 font-mono text-[9px] px-1.5 py-0.5 rounded bg-ink/10 text-ink uppercase">Sold Last</span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-ink">{mask('$' + r.price.toFixed(2))}</td>
                            <td className="px-3 py-2 font-mono text-xs text-inkMuted">{mask(r.shares)}</td>
                            <td className="px-3 py-2 font-mono text-xs text-ember">
                              {mask('$' + r.stop.toFixed(2))}
                              {isPattern && (
                                <span className="ml-1 font-mono text-[9px] text-inkMuted">(pattern)</span>
                              )}
                            </td>
                            <td className={`px-3 py-2 font-mono text-xs font-semibold ${pnlColor}`}>
                              {mask((r.pnl >= 0 ? '+' : '') + fmt(r.pnl))}
                            </td>
                            <td className={`px-3 py-2 font-mono text-xs ${pnlColor}`}>
                              {mask((r.pnlPct >= 0 ? '+' : '') + r.pnlPct.toFixed(2) + '%')}
                            </td>
                            <td className="px-3 py-2">
                              {r.role === 'absorbing' ? (
                                <span className="font-mono text-[10px] text-forest">✓ absorbing loss</span>
                              ) : (
                                <span className="font-mono text-[10px] text-inkMuted">exposing loss</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 5. FOUR SUMMARY RESULT BOXES */}
            {stopCalc && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <ResultBox
                  label="Capital Deployed"
                  value={mask(fmt(capitalDeployed))}
                  accent="text-forest"
                  sub={mask(`${filledTranches.length} of ${numTranches} tranches · ${fmt(Math.max(0, capitalRemaining))} remaining`)}
                />
                <ResultBox
                  label="Avg Cost Basis"
                  value={mask(`$${stopCalc.avgCost.toFixed(2)}`)}
                  accent="text-forest"
                  sub={mask(`${stopCalc.totalShares.toLocaleString()} total shares`)}
                />
                <ResultBox
                  label="True Net Risk at Stop"
                  value={mask(fmt(Math.abs(netAtStop)))}
                  accent={netAtStop >= 0 ? 'text-forest' : 'text-ember'}
                  sub={mask(`${Math.abs(netAtStopPct).toFixed(2)}% of equity`)}
                />
                <ResultBox
                  label="Stop Cushion"
                  value={mask(`${stopCushion.toFixed(2)}%`)}
                  accent="text-ink"
                  sub="avg cost → stop distance"
                />
              </div>
            )}

            {/* 6. BREAKEVEN STOP BOX */}
            {breakevenAnalysis && stopCalc && (
              <div className="bg-forest/5 border border-forest/20 rounded-xl px-5 py-4 font-mono">
                <div className="text-[10px] tracking-widest uppercase text-forest mb-3">
                  Breakeven Stop — $0 Net Risk
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Stop Price</div>
                    <div className="text-xl font-semibold text-ink">{mask('$' + breakevenAnalysis.breakevenPrice.toFixed(2))}</div>
                    <div className="text-[10px] text-inkMuted">= avg cost basis</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Cushion From Last Entry</div>
                    <div className="text-xl font-semibold text-ink">{mask(breakevenAnalysis.cushionFromLast.toFixed(1) + '%')}</div>
                    <div className="text-[10px] text-inkMuted">
                      {mask(`$${breakevenAnalysis.breakevenPrice.toFixed(2)} → $${breakevenAnalysis.lastEntry.toFixed(2)}`)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Requires Price to Rise</div>
                    {breakevenAnalysis.alreadyThere ? (
                      <div className="text-xl font-semibold text-forest">✓ ALREADY THERE</div>
                    ) : (
                      <>
                        <div className="text-xl font-semibold text-ink">{mask('+' + breakevenAnalysis.riseNeeded.toFixed(1) + '%')}</div>
                        <div className="text-[10px] text-inkMuted">
                          {mask(`from current stop $${effectiveStopPrice?.toFixed(2) ?? '—'} to breakeven`)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* LIFO Notice */}
            {costBasis === 'lifo' && filledTranches.length > 0 && (
              <div className="bg-canvas2 border border-border rounded-lg px-4 py-3 font-mono text-[10px] text-inkMuted">
                <strong className="text-ink">LIFO selected</strong> — Last-in, first-out. The most recently purchased shares are sold first.
                This may result in higher short-term capital gains taxes. Consult your tax advisor.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Position Project button */}
      <div className="flex justify-end">
        <button
          onClick={createFullBuildProject}
          disabled={saving || !stopCalc}
          className={`btn-primary flex items-center gap-1.5 ${!stopCalc ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {saving ? 'Creating...' : 'Create Position Project'}
        </button>
      </div>
    </div>
  )
}
